import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { fmtUsd } from "@/components/data-table";
import { ChainBadge } from "@/components/chain-badge";
import { CardImage } from "@/components/card-image";
import { ScrambleText } from "@/components/scramble-text";
import { makeWatchlistKey, useWatchlist } from "@/hooks/use-watchlist";

// Shared SlabScanner slab ERC-721 — same contract on both Base and ApeChain.
const SLAB_NFT_CONTRACT = "0x8a981c2cfdd7fbc65395dd2c02ead94e9a2f65a7";

/** Deep-link to the slab's current owner on OpenSea. Falls back to the item
 * page when we don't have an owner address yet (e.g. stale cache). OpenSea
 * supports ApeChain under the `ape_chain` slug, so the fallback works for
 * both networks. */
function ownerUrl(row: TopPullRow): string {
  if (row.owner) return `https://opensea.io/${row.owner}`;
  const slug = row.chain === "base" ? "base" : "ape_chain";
  return `https://opensea.io/assets/${slug}/${SLAB_NFT_CONTRACT}/${row.tokenId}`;
}

interface TopPullRow {
  slabId: string;
  chain: "base" | "apechain";
  machine: string;
  machineName: string;
  tokenId: string;
  description: string;
  imageUrl: string;
  /** Backup URLs from slab.cash (front/back of slab). Used as
   *  fallbacks if the primary arweave hash 404s. */
  imageUrls?: string[];
  rarity: string;
  valueUsd: number;
  marketValueUsd?: number;
  state: string;
  pullDate?: number;
  owner?: string;
}

interface Group {
  chain: "base" | "apechain";
  machine: string;
  machineName: string;
  priceUsd: number;
  top: TopPullRow[];
}

export default function TopPullsPage() {
  const [view, setView] = useState<"overall" | "per-pack">("overall");
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["top-pulls"],
    queryFn: async () =>
      (await (await fetch(`/api/top-pulls?perPack=8&limit=60`)).json()) as {
        overall: TopPullRow[];
        groups: Group[];
      },
  });

  return (
    <div className="p-3 md:p-4 space-y-3 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div className="space-y-1">
          <h1 className="text-xl font-bold uppercase tracking-[0.2em] neon-text cursor">
            <ScrambleText text="top pulls" duration={900} revealEach={45} />
          </h1>
          <p className="text-[11px] text-muted max-w-3xl font-mono">
            <span className="text-accent2">›</span> highest-value slabs pulled from every active pack
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded border border-border bg-panel/60 p-0.5 text-[11px] font-mono">
            {(["overall", "per-pack"] as const).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={
                  "px-3 py-1 uppercase tracking-wider transition-all " +
                  (view === v
                    ? "bg-accent/20 text-accent neon-text shadow-glow"
                    : "text-muted hover:text-accent2")
                }
              >
                {v}
              </button>
            ))}
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="rounded border border-border bg-panel/60 px-3 py-1 text-xs font-mono uppercase tracking-wider hover:border-accent hover:text-accent hover:shadow-glow transition-all disabled:opacity-50"
          >
            {isFetching ? "› syncing…" : "[refresh]"}
          </button>
        </div>
      </div>

      {isLoading ? (
        <Loading />
      ) : view === "overall" ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {(data?.overall ?? []).map((r, i) => (
            <CardTile key={r.slabId} row={r} rank={i + 1} />
          ))}
        </div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {(data?.groups ?? []).map((g) => (
            <div
              key={g.chain + g.machine}
              className="rounded-md border border-border bg-panel/70 p-3 brackets"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="font-bold text-accent neon-text uppercase tracking-wider text-sm">
                  {g.machineName}
                </div>
                <div className="flex items-center gap-2">
                  <ChainBadge chain={g.chain} />
                  <span className="text-xs text-muted font-mono">{fmtUsd(g.priceUsd)}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {g.top.map((p, i) => (
                  <CardTile key={p.slabId} row={p} rank={i + 1} compact />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CardTile({
  row,
  rank,
  compact,
}: {
  row: TopPullRow;
  rank: number;
  compact?: boolean;
}) {
  const rarityColor =
    row.rarity === "Legendary"
      ? "text-legendary"
      : row.rarity === "Mythic"
      ? "text-mythic"
      : row.rarity === "Rare"
      ? "text-rare"
      : row.rarity === "Uncommon"
      ? "text-uncommon"
      : "text-common";

  const { add, remove, isWatched } = useWatchlist();
  const watchKey = makeWatchlistKey({
    kind: "CARD",
    chain: row.chain,
    address: SLAB_NFT_CONTRACT,
    tokenId: row.tokenId,
  });
  const watched = isWatched(watchKey);

  function toggleWatch() {
    if (watched) {
      remove(watchKey);
      return;
    }
    add({
      kind: "CARD",
      chain: row.chain,
      chainId: row.chain === "base" ? 8453 : 33139,
      address: SLAB_NFT_CONTRACT,
      tokenId: row.tokenId,
      description: row.description,
      imageUrl: row.imageUrl,
      rarity: row.rarity,
      valueUsd: row.valueUsd,
    });
  }

  const href = ownerUrl(row);
  const ownerShort = row.owner
    ? `${row.owner.slice(0, 6)}…${row.owner.slice(-4)}`
    : null;

  return (
    <div
      className={
        "group relative flex flex-col overflow-hidden rounded border border-border bg-panel/80 " +
        "hover:border-accent hover:shadow-glow transition-all duration-200 animate-fadeIn"
      }
      title={row.description}
    >
      <div className="absolute top-1 left-1 z-10 rounded bg-bg/80 px-1.5 py-0.5 text-[10px] font-mono text-accent neon-text">
        #{String(rank).padStart(2, "0")}
      </div>
      <div className="absolute top-1 right-1 z-10">
        <ChainBadge chain={row.chain} />
      </div>
      {/* Image + description form one clickable region → owner's OpenSea
          profile. Watch button sits outside the link so its clicks don't
          navigate away. */}
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="flex flex-col"
        title={
          row.owner
            ? `Owned by ${row.owner} — view on OpenSea`
            : "View on OpenSea"
        }
      >
        <div className="relative bg-bg aspect-[3/4] overflow-hidden">
          <CardImage
            src={row.imageUrl}
            fallbackSrcs={row.imageUrls}
            alt={row.description}
          />
        </div>
        <div className="px-2 pt-2 text-[11px] font-mono">
          <div className="truncate text-text" title={row.description}>
            {row.description}
          </div>
        </div>
      </a>
      <div className="p-2 pt-1 space-y-1 text-[11px] font-mono">
        <div className="flex items-center justify-between">
          <span className={`uppercase tracking-wider text-[10px] ${rarityColor}`}>
            {row.rarity}
          </span>
          <span className="font-bold text-accent neon-text">
            {fmtUsd(row.valueUsd)}
          </span>
        </div>
        {!compact && row.marketValueUsd !== undefined && (
          <div className="text-[10px] text-muted">
            market: <span className="text-accent2">{fmtUsd(row.marketValueUsd)}</span>
          </div>
        )}
        <div className="flex items-center justify-between gap-1 pt-1">
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] font-mono text-muted hover:text-accent truncate"
            title={row.owner ?? undefined}
          >
            {ownerShort ? `owner ${ownerShort} ↗` : "view ↗"}
          </a>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleWatch();
            }}
            className={
              "shrink-0 whitespace-nowrap rounded border px-1.5 md:px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider transition-all " +
              (watched
                ? "border-gold/60 text-gold bg-gold/10 hover:bg-gold/20 shadow-[0_0_8px_rgba(255,176,32,0.4)]"
                : "border-border hover:border-accent hover:text-accent hover:shadow-glow")
            }
            title={watched ? "Remove from watchlist" : "Add to watchlist"}
            aria-label={watched ? "Remove from watchlist" : "Add to watchlist"}
          >
            {/* Compact star on mobile; full label on desktop so it stays
                readable in a wider card. */}
            <span className="md:hidden">{watched ? "★" : "☆"}</span>
            <span className="hidden md:inline">
              {watched ? "[watching ★]" : "[watch ☆]"}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div className="rounded border border-border bg-panel/60 p-6 text-sm text-muted font-mono">
      <span className="text-accent2">›</span> fetching big hits
      <span className="animate-blink text-accent ml-1">_</span>
    </div>
  );
}
